export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;
  if (body instanceof Blob || body instanceof ArrayBuffer || body instanceof Uint8Array) {
    payload = body as BodyInit;
    headers["Content-Type"] = "application/octet-stream";
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(url, { method, headers, body: payload, credentials: "same-origin", ...init });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch {}
    if (res.status === 401 && !url.startsWith("/api/auth/")) window.dispatchEvent(new CustomEvent("benleaf:unauthorized"));
    throw new ApiError(res.status, message);
  }
  const type = res.headers.get("content-type") ?? "";
  if (type.includes("application/json")) return (await res.json()) as T;
  return (await res.arrayBuffer()) as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<T>("PATCH", url, body ?? {}),
  del: <T>(url: string) => request<T>("DELETE", url),
  upload: <T>(url: string, data: Blob) => request<T>("POST", url, data),
  /** Like `upload`, but reports the bytes sent so far (fetch can't) and stops when `signal` aborts. */
  uploadWithProgress: <T>(url: string, data: Blob, onProgress: (sent: number) => void, signal?: AbortSignal) =>
    new Promise<T>((resolve, reject) => {
      if (signal?.aborted) return reject(new DOMException("Upload stopped.", "AbortError"));
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.setRequestHeader("Content-Type", "application/octet-stream");
      xhr.responseType = "json";
      xhr.upload.onprogress = (e) => onProgress(e.loaded);
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve(xhr.response as T);
        if (xhr.status === 401) window.dispatchEvent(new CustomEvent("benleaf:unauthorized"));
        reject(new ApiError(xhr.status, xhr.response?.error ?? `${xhr.status} ${xhr.statusText}`));
      };
      xhr.onerror = () => reject(new ApiError(0, "Couldn't reach the server."));
      xhr.onabort = () => reject(new DOMException("Upload stopped.", "AbortError"));
      signal?.addEventListener("abort", () => xhr.abort(), { once: true });
      xhr.send(data);
    }),
  text: async (url: string) => {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new ApiError(res.status, `${res.status}`);
    return res.text();
  },
};

// ---------------------------------------------------------------- shared types

export interface User {
  id: number;
  username: string;
  displayName: string;
  email: string;
  isAdmin: boolean;
  isTrusted: boolean;
  /** False for people a non-admin invited, until an admin verifies them. */
  verified: boolean;
  /** May invite people who don't have an account yet to projects they own. */
  canInvite: boolean;
  canCreateProjects: boolean;
  settings: UserSettings;
  /** Version of their profile picture (part of its URL), or null when they have none. */
  avatar: number | null;
}

/** For someone a non-admin invited: who brought them in, and who can verify them. */
export interface Verification {
  invitedBy: string | null;
  admins: string[];
}

export interface UserSettings {
  theme?: "light" | "dark" | "system";
  fontSize?: number;
  keymap?: "default" | "vim";
  spellLang?: "en-AU" | "en-GB" | "en-US" | "off";
  dictionary?: string[];
  autoCompile?: boolean;
  lineWrap?: boolean;
  pdfZoom?: string;
  /** Compile with images as placeholder boxes (much faster for image-heavy documents). */
  compileDraft?: boolean;
  /** Compile only the \include'd chapter being edited. */
  compileOnlyChapter?: boolean;
  /** Code shows the LaTeX source; visual shows headings, emphasis, maths and figures as they'll look. */
  editorMode?: "code" | "visual";
  /** The last "What's new" release this account has seen, so the card pops up once per account. */
  whatsNewSeen?: string;
}

export type Role = "owner" | "editor" | "viewer";

export interface SyncState {
  status?: "ok" | "conflict" | "error";
  conflictBranch?: string;
  lastError?: string;
  lastSyncAt?: number;
  lastPushAt?: number;
  lastCommitAt?: number;
  behind?: number;
  lastFetchAt?: number;
  /** The last sync where both sides changed the same files and benleaf's version won. Admins only. */
  kept?: { at: number; files: string[] };
}

export interface Project {
  id: string;
  name: string;
  rootDoc: string;
  compiler: "pdflatex" | "xelatex" | "lualatex";
  shellEscape: boolean;
  trackAll: boolean;
  /** Syncs with the server (admins can see where via RemoteInfo). */
  linked: boolean;
  autoSync: boolean;
  syncState: SyncState;
  wordTarget: number;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectListItem extends Project {
  role: Role;
  ownerName: string;
}

/** A project in its owner's recycle bin. */
export interface TrashedProject {
  id: string;
  name: string;
  linked: boolean;
  deletedAt: number;
  /** When the server deletes it for good. */
  purgeAt: number;
}

export interface TreeEntry {
  path: string;
  type: "file" | "folder";
  size?: number;
  docId?: string;
}

export interface Member {
  id: number;
  username: string;
  displayName: string;
  role: Role;
}

export interface LogEntry {
  level: "error" | "warning" | "typesetting";
  message: string;
  file?: string;
  line?: number;
  content?: string;
  source?: string;
}

/** Someone invited to a project who hasn't created their account yet. */
export interface PendingInvite {
  id: string;
  name: string;
  role: "editor" | "viewer";
  expiresAt: number;
  expired: boolean;
  createdBy: number | null;
}

export interface CompileResult {
  buildId: string;
  status: "success" | "failure" | "timeout" | "error" | "cancelled";
  pdf: boolean;
  entries: LogEntry[];
  counts: { errors: number; warnings: number; typesetting: number };
  startedAt: number;
  durationMs: number;
  rootDoc: string;
  compiler: string;
  by?: string;
  message?: string;
  draft: boolean;
  only: string | null;
  onlyRequested: boolean;
  note?: string;
  words?: CompileWordCount;
  /** Edits made before this moment (server time) are in the build. */
  snapshotAt?: number;
  /** Who stopped it, for a cancelled compile. */
  stoppedBy?: string;
}

/** A compile that's waiting or running on the server. Times are the server's. */
export interface CompileJob {
  id: string;
  draft: boolean;
  /** The file whose chapter was asked for ("only the chapter I'm editing"), else null. */
  only: string | null;
  force: boolean;
  by: string | null;
  requestedAt: number;
  /** Null while it waits behind another compile of this project. */
  startedAt: number | null;
  /** Edits made before this moment are in the compile. Null until its files are copied. */
  snapshotAt: number | null;
  phase: "queued" | "preparing" | "waiting" | "typesetting" | "finishing";
  /** Who asked it to stop, once someone has. */
  stoppedBy: string | null;
  /** A full compile, which everyone compiling in full mode sees and joins. Otherwise it's this person's own. */
  shared: boolean;
  /** Waiting behind a compile that isn't shown: someone else's draft or chapter one, or (in those modes) a full one. */
  behindOthers: boolean;
}

/** The count taken with every compile (the word progress dialog). */
export interface CompileWordCount {
  total: number;
  text: number;
  headers: number;
  captions: number;
  floats: number;
  mathInline: number;
  mathDisplay: number;
  sections: Array<{ kind: string; title: string; text: number; headers: number; captions: number }>;
  problems: string[];
}

export interface ProjectDetails {
  project: Project;
  role: Role;
  tree: TreeEntry[];
  members: Member[];
  pendingInvites: PendingInvite[];
  online: number[];
  compile: CompileResult | null;
  compiling: boolean;
  compileJobs: CompileJob[];
  /** When the project's files last changed (server time), if since the server started. */
  editedAt: number | null;
  /** Where you left this project; null if you haven't opened anything in it yet. */
  view: ProjectView | null;
}

export interface ProjectView {
  openPath: string | null;
  /** Folder path -> expanded. */
  expanded: Record<string, boolean>;
  /** Files open as tabs, in order. Missing from views saved before tabs existed. */
  openTabs?: string[];
  /** Doc id -> where you were in that file. */
  positions?: Record<string, DocPosition>;
  /** Where you were in the PDF. */
  pdf?: PdfPosition | null;
}

/** Offsets into the doc, plus how far (px) the view was scrolled past the line at its top. */
export interface DocPosition {
  anchor: number;
  head: number;
  /** Start of the line at the top of the view. */
  top: number;
  offset: number;
}

/** The page at the top of the view and the point on it at the view's top-left, in PDF points, so zoom doesn't matter. */
export interface PdfPosition {
  page: number;
  left: number;
  top: number;
}

export interface HistoryEntry {
  sha: string;
  parents: string[];
  authorName: string;
  authorEmail: string;
  /** The account that made it, when its email matches one. */
  authorId: number | null;
  date: number;
  subject: string;
  body: string;
  labels: Array<{ tag: string; name: string }>;
  /** The first few files this version changed, out of fileCount. */
  files: Array<{ status: "A" | "M" | "D"; path: string }>;
  fileCount: number;
}

export interface ProjectIndex {
  labels: Array<{ label: string; file: string; line: number; context?: string }>;
  citations: Array<{ key: string; file: string; line?: number; title?: string; author?: string; year?: string; type: string }>;
  files: string[];
  commands: string[];
  /** Labels and bibliography keys LaTeX knew about in the last full compile (including ones made by macros). */
  compiled: { labels: string[]; citations: string[] };
}

export interface SearchMatch {
  line: number;
  column: number;
  length: number;
  before: string;
  text: string;
  after: string;
}

export interface SearchResults {
  files: Array<{ path: string; docId: string; matches: SearchMatch[] }>;
  total: number;
  truncated: boolean;
}

export interface WordCountRow {
  /** "Part", "Chapter", "Section", ... or "" for text before the first heading. */
  kind: string;
  title: string;
  text: number;
  headers: number;
  captions: number;
  floats: number;
  mathInline: number;
  mathDisplay: number;
}

export interface WordCount {
  rootDoc: string;
  total: WordCountRow;
  sections: WordCountRow[];
  warnings: string[];
  countedAt: number;
}

/** Where a linked project syncs to. Only sent to admins. */
/** Where a project syncs to. Only ever sent to admins. */
export interface RemoteInfo {
  name: string;
  branch: string;
  url: string;
  conflictBranch?: string;
  lastError?: string;
  kept?: { at: number; files: string[] };
}

export interface SyncRepo {
  fullName: string;
  name: string;
  private: boolean;
  defaultBranch: string;
  updatedAt: string;
  description: string | null;
  empty: boolean;
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
